<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ROLES } from "@/services/acl";
import {
  getMediaUrl,
  fetchMediaComplianceRules,
  createMediaComplianceRule,
  addMediaComplianceRuleMedia,
  vectorizeMediaComplianceRuleImageFromMediaPath,
} from "@/services/backofficeClient";

const props = defineProps({
  photos: { type: Array, default: () => [] },
  userId: { type: [String, Number], default: "" },
  userName: { type: String, default: "" },
});
const emit = defineEmits(["status"]);

const router = useRouter();
const store = useStore();
const canUseBiometricSearch = computed(() =>
  store.getters.hasRole(ROLES.BIOMETRIC_SEARCH)
);
const canCreateMediaComplianceRule = computed(() =>
  store.getters.hasRole(ROLES.MEDIA_COMPLIANCE_RULE_CREATE)
);
const canEditMediaComplianceRule = computed(() =>
  store.getters.hasRole(ROLES.MEDIA_COMPLIANCE_RULE_EDIT)
);
const canManageMediaComplianceRules = computed(
  () =>
    canUseBiometricSearch.value &&
    (canCreateMediaComplianceRule.value || canEditMediaComplianceRule.value)
);

const DEFAULT_CNN_THRESHOLD = 0.95;
const DEFAULT_FACE_THRESHOLD = 0.8;
const defaultVectorThreshold = (type) =>
  type === "face" ? DEFAULT_FACE_THRESHOLD : DEFAULT_CNN_THRESHOLD;

const showModal = ref(false);
const targetPhotos = ref([]);
const photoKey = (photo) => photo.id || photo.mediaPath;
const selectedPhotoKeys = ref(new Set());
const selectedPhotos = computed(() =>
  targetPhotos.value.filter((photo) => selectedPhotoKeys.value.has(photoKey(photo)))
);
const isPhotoSelected = (photo) => selectedPhotoKeys.value.has(photoKey(photo));
const togglePhoto = (photo) => {
  const key = photoKey(photo);
  const next = new Set(selectedPhotoKeys.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  selectedPhotoKeys.value = next;
};

const mode = ref("new");
const newRuleName = ref("");
const rules = ref([]);
const rulesLoading = ref(false);
const selectedRuleId = ref("");
const saving = ref(false);
const progress = ref("");
const error = ref("");

const openModal = async () => {
  if (!props.photos.length) return;
  targetPhotos.value = props.photos;
  selectedPhotoKeys.value = new Set(props.photos.map(photoKey));
  mode.value = canCreateMediaComplianceRule.value ? "new" : "existing";
  newRuleName.value = props.userName || "";
  selectedRuleId.value = "";
  error.value = "";
  progress.value = "";
  showModal.value = true;

  rulesLoading.value = true;
  try {
    const payload = await fetchMediaComplianceRules();
    rules.value = Array.isArray(payload) ? payload : [];
  } catch (err) {
    console.error("Failed to fetch media compliance rules", err);
    error.value =
      err?.payload?.message ||
      err?.message ||
      "Failed to load media compliance rules.";
  } finally {
    rulesLoading.value = false;
  }
};

const closeModal = () => {
  if (saving.value) return;
  showModal.value = false;
  targetPhotos.value = [];
  selectedPhotoKeys.value = new Set();
};

const submit = async () => {
  const photos = selectedPhotos.value;

  error.value = "";
  if (!photos.length) {
    error.value = "Select at least one photo.";
    return;
  }
  if (mode.value === "new" && !newRuleName.value.trim()) {
    error.value = "Rule name is required.";
    return;
  }
  if (mode.value === "existing" && !selectedRuleId.value) {
    error.value = "Select a rule.";
    return;
  }

  saving.value = true;
  try {
    const newMediaList = [];
    const failures = [];
    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      progress.value = `Vectorizing photo ${i + 1} of ${photos.length}...`;
      try {
        const vectorized = await vectorizeMediaComplianceRuleImageFromMediaPath(photo.mediaPath);
        const vectors = Array.isArray(vectorized?.vectors) ? vectorized.vectors : [];
        const faceCount = vectors.filter((v) => v.type === "face").length;
        newMediaList.push({
          imagePath: vectorized?.imagePath || "",
          label: `User ${props.userId || ""} photo`,
          vectors: vectors
            .filter((v) => v.type !== "face" || faceCount <= 1)
            .map((v) => ({
              type: v.type,
              vector: v.vector || [],
              threshold: defaultVectorThreshold(v.type),
            })),
        });
      } catch (err) {
        console.error("Failed to vectorize photo", err);
        failures.push(err?.payload?.message || err?.message || "Failed to vectorize photo.");
      }
    }
    progress.value = "";

    if (!newMediaList.length) {
      error.value = failures.length
        ? failures.join("; ")
        : "Failed to vectorize photos.";
      return;
    }

    const photoWord = newMediaList.length === 1 ? "photo" : "photos";
    let savedRuleId;
    let message;
    if (mode.value === "new") {
      const created = await createMediaComplianceRule({
        name: newRuleName.value.trim(),
        media: newMediaList,
        actions: [{ type: "SetUserStatus", status: "ghosted" }],
      });
      savedRuleId = created?.id;
      message = `Media compliance rule created with ${newMediaList.length} ${photoWord}.`;
    } else {
      await addMediaComplianceRuleMedia(selectedRuleId.value, newMediaList);
      savedRuleId = selectedRuleId.value;
      message = `Added ${newMediaList.length} ${photoWord} to media compliance rule.`;
    }
    if (failures.length) {
      message += ` (${failures.length} failed: ${failures.join("; ")})`;
    }
    emit("status", { message, type: "success" });
    showModal.value = false;
    targetPhotos.value = [];
    selectedPhotoKeys.value = new Set();
    if (savedRuleId) {
      router.push({ name: "MediaComplianceRules", query: { ruleId: savedRuleId } });
    }
  } catch (err) {
    console.error("Failed to save media compliance rule", err);
    error.value =
      err?.payload?.message ||
      err?.message ||
      "Failed to save media compliance rule.";
  } finally {
    saving.value = false;
    progress.value = "";
  }
};
</script>

<template>
  <button v-if="canManageMediaComplianceRules" type="button" class="btn btn-sm btn-outline-warning mb-0"
    :disabled="photos.length === 0" @click="openModal">
    🛡️ Make compliance rule
  </button>

  <div v-if="showModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h6 class="modal-title">
            Add {{ selectedPhotos.length }} of {{ targetPhotos.length }}
            photo{{ targetPhotos.length === 1 ? "" : "s" }} to media compliance rule
          </h6>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModal"
            :disabled="saving"></button>
        </div>
        <div class="modal-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <p class="text-xs text-secondary mb-0">Click a photo to include or exclude it.</p>
            <div class="btn-group btn-group-sm">
              <button type="button" class="btn btn-outline-secondary" :disabled="saving"
                @click="selectedPhotoKeys = new Set(targetPhotos.map(photoKey))">
                Select all
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="saving"
                @click="selectedPhotoKeys = new Set()">
                Deselect all
              </button>
            </div>
          </div>
          <div class="d-flex flex-wrap gap-2 mb-3">
            <div v-for="photo in targetPhotos" :key="photo.id || photo.mediaPath"
              class="position-relative" style="width: 56px; height: 56px; cursor: pointer"
              @click="!saving && togglePhoto(photo)">
              <img :src="getMediaUrl(photo.mediaPath)" alt="target photo" class="rounded border w-100 h-100"
                :class="isPhotoSelected(photo) ? 'border-success border-2' : ''"
                :style="{ objectFit: 'cover', opacity: isPhotoSelected(photo) ? 1 : 0.35 }" />
              <span v-if="isPhotoSelected(photo)"
                class="position-absolute d-flex align-items-center justify-content-center bg-success text-white rounded-circle"
                style="width: 16px; height: 16px; top: -4px; right: -4px; font-size: 10px">
                &#10003;
              </span>
            </div>
          </div>

          <div class="btn-group btn-group-sm mb-2 w-100" role="group">
            <button type="button" class="btn"
              :class="mode === 'new' ? 'btn-success' : 'btn-outline-success'"
              :disabled="!canCreateMediaComplianceRule || saving"
              @click="mode = 'new'">
              New rule
            </button>
            <button type="button" class="btn"
              :class="mode === 'existing' ? 'btn-success' : 'btn-outline-success'"
              :disabled="!canEditMediaComplianceRule || saving"
              @click="mode = 'existing'">
              Add to existing
            </button>
          </div>

          <div v-if="mode === 'new'">
            <label class="form-label text-sm">Rule name</label>
            <input type="text" class="form-control" v-model="newRuleName"
              placeholder="e.g. Known banned image set" :disabled="saving" />
          </div>
          <div v-else>
            <label class="form-label text-sm">Rule</label>
            <select class="form-select" v-model="selectedRuleId"
              :disabled="rulesLoading || saving">
              <option value="" disabled>
                {{ rulesLoading ? "Loading rules..." : "Select a rule" }}
              </option>
              <option v-for="rule in rules" :key="rule.id" :value="rule.id">
                {{ rule.name }} ({{ rule.mediaCount || 0 }} photos)
              </option>
            </select>
          </div>

          <p v-if="progress" class="text-xs text-secondary mt-3 mb-0">
            {{ progress }}
          </p>
          <p v-if="error" class="text-danger text-xs mt-3 mb-0">{{ error }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="closeModal"
            :disabled="saving">
            Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="submit"
            :disabled="saving || !selectedPhotos.length">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            {{ mode === "new" ? "Create rule" : "Add photos" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
